// © Microsoft Corporation. All rights reserved.

import { AppError, WebWorkerErrors, logger, stringifyError, tryGetPerfTimeline, getAppErrorsPropertyBag } from "@msnews/diagnostics";

import { canUseDOM } from "@msnews/isomorphic-util";

declare global {
    /** Adding webWorker to Window */
    interface Window {
        /** Global flag indicating if web worker is present */
        _isWebWorkerPresent: boolean;

        /** Web worker instance */
        webWorker?: Worker;

        /** Web worker runtime errors */
        webWorkerErrors?: any[];

        /** Web worker messages */
        webWorkerMessages?: any[];
    }
}

/** Web worker request interface. */
export interface WebWorkerRequest {
    /** The message id. */
    id: string;

    /** The type of message. */
    type?: string;

    /** The additional message payload */
    payload?: any;
}

/** Web worker response interface. */
export interface WebWorkerResponse<T = any> {
    /** The message id. */
    id: string;

    /** The item fetched status. */
    fetched: boolean;

    /** The item payload. */
    payload: T;
}

/**
 * Reports web worker presence.
 *
 * **NOTE**: Make sure to update the **_window._isWebWorkerPresent_** flag as well
 * while updating this, since the diagnostics lib doesn't have access to the core lib.
 */
export let isWebWorkerPresent: boolean;

/** Web-worker message-map entry */
export interface WebWorkerMessageMapEntry {
    /** The message type (default is "data") */
    type: string;

    /** The callback, if any, that receives the data payload for the message */
    callback: (data: any) => void;
}

/** The message map from message id to the WebWorkerMessageMapEntry */
const messages = new Map<string, WebWorkerMessageMapEntry>();

let logAppError: (data: AppError) => void;

/** The perf timeline for the web-worker interface */
const perfTimeline = tryGetPerfTimeline("WebWorkerUtility", "WebWorkerUtility", true);

/** Map to save messages that were sent from web worker but were not requested by main thread */
const standbyMessages = new Map<string, WebWorkerResponse>();

/**
 * Initialize isWebWorkerPresent. And this function also serve as test hook.
 * @param override  - The test override
 */
export function init(override?: boolean): void {
    isWebWorkerPresent = false;
    if (canUseDOM()) {
        isWebWorkerPresent = (typeof override !== "undefined" ? override : !!window.webWorker);
        window._isWebWorkerPresent = isWebWorkerPresent;
    }

    if (!isWebWorkerPresent) {
        return;
    }

    window.webWorker.onmessage = (event: MessageEvent) => {
        const { data: response } = event;
        const pendingMessage = messages.get(response.id);

        if (!pendingMessage) {
            standbyMessages.set(response.id, response);
            return;
        }

        if (pendingMessage.type === "data") {
            messages.delete(response.id);
        }

        const callback = pendingMessage.callback;
        if (callback) {
            callback(response);
        }
    };

    // Process messages received before main thread initialized
    setTimeout(() => {
        if (window.webWorkerMessages && Array.isArray(window.webWorkerMessages)) {
            window.webWorkerMessages.forEach((response: WebWorkerResponse) => {
                if (!response.id) {
                    return;
                }
                standbyMessages.set(response.id, response);
            });

            delete window.webWorkerMessages;
        }
    });
}

/**
 * This function utility is converting web worker's postMessage communication into an async function.
 * It's sends given request to web worker, returns a promise, and promise is resolved when response is received from web worker.
 * @param request - The message to send
 * @param timeoutInMs(optional) - Timeout in milliseconds after which the promise will resolve with the default(failed) web worker response
 * @returns A promise to return WebWorkerResponse
 */
export function getFromWebWorker(request: WebWorkerRequest, timeoutInMs?: number): Promise<WebWorkerResponse> {
    const defaultResponse = {
        id: (request && request.id) || "",
        fetched: false,
        payload: null
    };

    if (!isWebWorkerPresent || !request || !request.id) {
        return Promise.resolve(defaultResponse);
    }

    const endGetFromWebWorker = perfTimeline && perfTimeline.startMeasure(request.id).endMeasure;

    // Check if web worker already sent the message with data and return it.
    const standbyMessageData = standbyMessages.get(request.id);

    if (standbyMessageData) {
        // Delete the message to avoid returning the same data for future requests
        standbyMessages.delete(request.id);

        if (endGetFromWebWorker) {
            endGetFromWebWorker("standbymessage");
        }

        return Promise.resolve(standbyMessageData);
    }

    let promiseTimeout;
    let timeOutPromise;

    // If timeout is provided, create a promise that will resolve after the timeout expires
    if (timeoutInMs) {
        timeOutPromise = new Promise<WebWorkerResponse>(resolve => {
            promiseTimeout = setTimeout(
                () => {
                    // Request timed out, we need not process the response if it's returned later, remove from messages map
                    messages.delete(request.id);

                    const errorMessage = `Web worker request timed out after ${timeoutInMs}ms for request id: [${request.id}]`;

                    if (logAppError) {
                        logAppError({
                            ...WebWorkerErrors.MessageTimeout,
                            message: "Web worker request timed out",
                            pb: {
                                ...WebWorkerErrors.MessageTimeout.pb,
                                customMessage: errorMessage
                            }
                        });
                    } else {
                        logger.logError(errorMessage);
                    }

                    if (endGetFromWebWorker) {
                        endGetFromWebWorker("timeout");
                    }

                    resolve(defaultResponse);
                },
                timeoutInMs);
        });
    }

    const webWorkerPromise: Promise<WebWorkerResponse> = new Promise(
        resolve => {
            const onMessage = (response: WebWorkerResponse) => {
                // We got a response from web worker, clear the promise timeout if it exists.
                if (promiseTimeout) {
                    clearTimeout(promiseTimeout);
                }

                if (endGetFromWebWorker) {
                    endGetFromWebWorker();
                }

                resolve(response);
            };

            messages.set(request.id, {
                type: request.type || "data",
                callback: onMessage
            });
        }
    );

    window.webWorker.postMessage({
        id: request.id,
        type: request.type || "data",
        payload: request.payload
    });

    return timeoutInMs
        ? Promise.race([webWorkerPromise, timeOutPromise])
        : webWorkerPromise;
}

/**
 * Sends given request to the web worker.
 * @param request - The message to send
 */
export function sendToWebWorker(request: WebWorkerRequest): void {
    if (!isWebWorkerPresent || !request || !request.id) {
        return;
    }

    window.webWorker.postMessage({
        id: request.id,
        type: request.type || "command",
        payload: request.payload || null
    });
}

/**
 * Subscribes to web worker notification
 * @param request subscription request
 * @param callback the callback to execute when notification is received
 */
export function subscribeWebWorkerNotification(request: WebWorkerRequest, callback: (payload: any) => void): void {
    if (!isWebWorkerPresent || !request || request.type !== "subscription" || !callback) {
        return;
    }

    messages.set(request.id, { type: "subscription", callback });
    window.webWorker.postMessage(request);
}

/**
 * Sets the AppError logging function and adds event listener for web worker errors.
 * @param handler - Error tracker function
 */
export function trackWebWorkerErrors(trackAppErrorEvent: (data: AppError) => void): void {
    if (!isWebWorkerPresent || !trackAppErrorEvent || typeof trackAppErrorEvent !== "function") {
        return;
    }

    logAppError = function (data: AppError): void {
        try {
            trackAppErrorEvent(data);
        } catch (ex) {
            logger.logError(`Failed to log web worker AppError. Error: ${stringifyError(ex)}`);
        }
    };

    // callback for unhandled runtime exceptions
    window.webWorker.onerror = onWebWorkerRuntimeError;

    // Callback for handled errors(logger.error) in web worker
    const onWorkerErrorMessage = (response: WebWorkerResponse) => {
        if (response.payload && typeof response.payload === "object") {
            const wwAppError = response.payload as AppError;

            // Since web worker is not aware of certain property bag values, merge it with current values.
            wwAppError.pb = {
                ...wwAppError.pb,
                ...getAppErrorsPropertyBag()
            };

            logAppError(wwAppError);
        } else {
            logAppError({
                ...WebWorkerErrors.HandledError,
                message: "Web worker error",
                pb: {
                    ...WebWorkerErrors.HandledError.pb,
                    customMessage: JSON.stringify(response.payload || "")
                }
            });
        }
    };

    subscribeWebWorkerNotification({ id: "WebWorkerError", type: "subscription" }, onWorkerErrorMessage);

    // Skip the thread to capture any errors in the queue
    setTimeout(() => {
        // Read and log web worker errors that were reported before the main thread initialized.
        if (window.webWorkerErrors && Array.isArray(window.webWorkerErrors)) {
            window.webWorkerErrors.forEach(error => {
                onWebWorkerRuntimeError(error);
            });

            delete window.webWorkerErrors;
        }
    });
}

/**
 * Handler for web worker error events
 * @param event web worker ErrorEvent
 */
function onWebWorkerRuntimeError(event: ErrorEvent): void {
    // If ErrorEvent does not contain the filename, only possible reason could be that the web worker script 404ed.
    if (!event.filename) {
        isWebWorkerPresent = false;
        window._isWebWorkerPresent = isWebWorkerPresent;
        window.webWorker.terminate();

        logAppError({
            ...WebWorkerErrors.FileNotFound,
            message: "Web worker script not found",
            pb: {
                ...WebWorkerErrors.FileNotFound.pb,
                customMessage: JSON.stringify(getErrorDetails(event))
            }
        });

        return;
    }

    logAppError({
        ...WebWorkerErrors.RuntimeError,
        message: "Web worker runtime error",
        pb: {
            ...WebWorkerErrors.RuntimeError.pb,
            customMessage: JSON.stringify(getErrorDetails(event))
        }
    });
}

/** Extracts properties from error event. Direct JSON.stringify removes all properties except `isTrusted` */
function getErrorDetails(error: ErrorEvent): any {
    return JSON.stringify({
        filename: error.filename,
        message: error.message,
        lineno: error.lineno
    });
}

init();
